/**
 * 🏛️ Main Layout — Sidebar + Header
 *
 * RTL by default (Arabic).
 * Smooth animations.
 */

import { useState } from 'react';
import {
  Outlet,
  NavLink,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Building2,
  Calendar,
  Star,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  BarChart3,
  Shield,
} from 'lucide-react';

import { Logo } from '@presentation/components/ui/Logo';
import { NotificationBell } from '@presentation/components/notifications/NotificationBell';
import { TrialBanner } from '@presentation/components/subscription/TrialBanner';
import { useUser } from '@application/stores/auth.store';
import { authService } from '@application/services/AuthService';
import { cn } from '@shared/utils/cn';
import type { ProviderPermission } from '@domain/entities/ProviderUser';

interface NavItem {
  to: string;
  label: string;
  icon: typeof Home;
  exact?: boolean;
  adminOnly?: boolean;
  /** 🔒 super_admin فقط (مثل سجل التدقيق — الـ rules تقصر قراءته عليه) */
  superAdminOnly?: boolean;
  providerOwnerOnly?: boolean;
  /** 🆕 يظهر لكل من admin و provider_owner */
  providerOrAdmin?: boolean;
  permission?: ProviderPermission;
}

const navItems: NavItem[] = [
  { to: '/', label: 'الرئيسية', icon: Home, exact: true },
  { to: '/venues', label: 'قاعاتي', icon: Building2 },
  { to: '/calendar', label: 'التقويم', icon: Calendar },
  { to: '/bookings', label: 'الحجوزات', icon: Calendar },
  { to: '/analytics', label: 'التحليلات', icon: BarChart3 },
  { to: '/reviews', label: 'التقييمات', icon: Star, permission: 'reviews.read' },
  { to: '/customers', label: 'العملاء', icon: Star, providerOwnerOnly: true },
  { to: '/blocked-dates', label: '🚫 الأيام المحظورة', icon: Calendar, providerOwnerOnly: true },
  { to: '/bulk-pricing', label: '💰 تعديل الأسعار جماعياً', icon: BarChart3, providerOwnerOnly: true },
  { to: '/promotions', label: '🎁 العروض الترويجية', icon: Star, providerOrAdmin: true },
  { to: '/team', label: '👥 فريق العمل', icon: Shield, providerOwnerOnly: true },
  { to: '/provider-settings', label: '⚙️ بيانات المؤسسة', icon: Settings, providerOwnerOnly: true },
  { to: '/subscription', label: '💎 الاشتراك', icon: Star, providerOwnerOnly: true },
  { to: '/settings', label: 'الإعدادات', icon: Settings },
  { to: '/admin', label: 'لوحة الإدارة', icon: Shield, adminOnly: true },
  { to: '/admin/providers', label: '🏛️ المزودون', icon: Building2, adminOnly: true },
  { to: '/admin/notifications', label: '🔔 إشعارات النظام', icon: Shield, adminOnly: true },
  { to: '/admin/featured-venues', label: '⭐ القاعات المميّزة', icon: Star, adminOnly: true },
  { to: '/admin/analytics', label: '📊 التحليلات المتقدّمة', icon: BarChart3, adminOnly: true },
  { to: '/admin/cities', label: '🏙️ إدارة المدن', icon: Building2, adminOnly: true },
  { to: '/admin/subscriptions', label: '💎 إدارة الاشتراكات', icon: Star, adminOnly: true },
  { to: '/admin/calendar-config', label: '📆 تصنيف أيام السنة', icon: Calendar, superAdminOnly: true },
  { to: '/admin/audit-log', label: '📜 سجل التدقيق', icon: Shield, superAdminOnly: true },
];

export default function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUser();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = async () => {
    await authService.signOut();
    navigate('/login', { replace: true });
  };

  // فلترة nav حسب الصلاحيات
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isProviderOwner = user?.role === 'provider_owner';
  const visibleNavItems = navItems.filter((item) => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.superAdminOnly && user?.role !== 'super_admin') return false;
    if (item.providerOwnerOnly && !isProviderOwner) return false;
    // 🆕 يظهر لكل من admin و provider_owner (مثل العروض الترويجية)
    if (item.providerOrAdmin && !isAdmin && !isProviderOwner) return false;
    if (
      item.permission &&
      !isAdmin &&
      !user?.permissions?.includes(item.permission)
    ) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-purple-50/30 to-brand-gold-50/20" dir="rtl">
      {/* ─── Sidebar Desktop ─── */}
      <aside className="hidden lg:flex fixed inset-y-0 right-0 w-64 flex-col bg-white border-l border-brand-purple-100 z-30">
        <div className="p-6 border-b border-brand-purple-100 flex items-center justify-between">
          <Logo size="sm" />
          <NotificationBell />
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact ?? false}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all',
                  isActive
                    ? 'bg-gradient-royal text-white shadow-elegant'
                    : 'text-brand-purple-600 hover:bg-brand-purple-50'
                )
              }
            >
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User Menu */}
        <div className="p-4 border-t border-brand-purple-100 relative">
          <button
            onClick={() => setUserMenuOpen((s) => !s)}
            className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-brand-purple-50 transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-gradient-royal flex items-center justify-center text-white font-bold">
              {user?.displayName?.charAt(0) ?? user?.email?.charAt(0) ?? '👤'}
            </div>
            <div className="flex-1 text-right min-w-0">
              <p className="text-sm font-semibold text-brand-purple-700 truncate">
                {user?.displayName ?? 'مستخدم'}
              </p>
              <p className="text-xs text-brand-purple-400 truncate">{user?.email}</p>
            </div>
            <ChevronDown className={cn('h-4 w-4 text-brand-purple-400 transition-transform', userMenuOpen && 'rotate-180')} />
          </button>

          <AnimatePresence>
            {userMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="absolute bottom-full left-4 right-4 mb-2 bg-white rounded-xl shadow-elegant border border-brand-purple-100 p-2"
              >
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  تسجيل الخروج
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </aside>

      {/* ─── Mobile Header ─── */}
      <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-brand-purple-100 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 rounded-lg hover:bg-brand-purple-50"
          aria-label="فتح القائمة"
        >
          <Menu className="h-5 w-5 text-brand-purple-600" />
        </button>
        <Logo size="sm" showText={false} />
        <NotificationBell />
      </header>

      {/* ─── Mobile Sidebar (Drawer) ─── */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="lg:hidden fixed inset-y-0 right-0 w-72 bg-white z-50 flex flex-col"
            >
              <div className="p-6 border-b border-brand-purple-100 flex items-center justify-between">
                <Logo size="sm" />
                <button onClick={() => setMobileOpen(false)} aria-label="إغلاق">
                  <X className="h-5 w-5 text-brand-purple-600" />
                </button>
              </div>
              <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                {visibleNavItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.exact ?? false}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all',
                        isActive
                          ? 'bg-gradient-royal text-white'
                          : 'text-brand-purple-600 hover:bg-brand-purple-50'
                      )
                    }
                  >
                    <item.icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
                <button
                  onClick={handleLogout}
                  className="w-full mt-4 flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut className="h-5 w-5" />
                  تسجيل الخروج
                </button>
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ─── Main Content ─── */}
      <main className="lg:mr-64">
        {/* 💎 Trial Banner — يظهر للـ providers في trial */}
        <TrialBanner />

        {/*
          لا نستخدم AnimatePresence mode="wait" حول Outlet؛ لأن React Router
          يبدّل محتوى Outlet أثناء خروج الغلاف القديم، وقد يترك الصفحة الجديدة
          فارغة عند التنقل السريع أو عند تحميل صفحة lazy بطيئة.

          نحافظ على حركة الدخول فقط، بينما تُستبدل الصفحة السابقة فوراً.
        */}
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          <Outlet />
        </motion.div>
      </main>
    </div>
  );
}
